import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { isPendoAllowed } from '@thoughtspot/pendo';
import React, { useEffect, useState } from 'react';
import { useLocation, useMatches } from 'react-router';
import { useAppContext } from '/@contexts/global-app-context';
import {
    useAddUserToModeMutation,
    useGetAnalystStudioAccessInfoLazyQuery,
} from '/@services/analyst-studio/analyst-service';
import {
    isCurrentPrintViewPath,
    removeOverrideOrgId,
} from '/@services/system/nav-service/nav-service';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import {
    Application,
    ApplicationList,
    Applications,
    ROUTES,
} from '../../routing/routes';
import {
    NavLinkCallback,
    NavLinkConfig,
    NavLinkWithTitleIconAndCallback,
    QuickLinkItem,
} from './global-nav';
import { GlobalNavV3 } from './global-nav-v3';
import { getComputedNavThemeType } from './navigation/left-navigation.util';

const Logger = create('top-nav');

interface NavContainerProps {
    isCopilotEnabled?: boolean;
    customQuickLinks?: QuickLinkItem[];
}

const NavContainer: React.FC<NavContainerProps> = ({
    isCopilotEnabled = false,
    customQuickLinks = [],
}) => {
    const {
        navService,
        sessionService,
        customStylingService,
    } = useAppContext();
    const { t } = useTranslation();
    const matches = useMatches();
    const location = useLocation();

    const isPrintPage = isCurrentPrintViewPath();
    const isEmbedded = isEmbeddedApp();
    const isAllowedInLDExceptionList =
        isFeatureEnabled(
            FEATURE_FLAG_NAMES.ENABLE_PENDO_FROM_EXCEPTION_LIST,
        ) === true;
    const isNotAProductionCluster = sessionService.isNotAProductionCluster();

    const shouldShowPendoHelp =
        sessionService.isPendoEnabled() &&
        isFeatureEnabled(FEATURE_FLAG_NAMES.ENABLE_PENDO_HELP) &&
        isPendoAllowed(
            isPrintPage,
            isEmbedded,
            isAllowedInLDExceptionList,
            isNotAProductionCluster,
        );

    const currentAppRoute = matches.find(
        match => (match.handle as any)?.handle,
    );

    // `matches` is ordered outermost-first, so the innermost route's
    // declaration wins. The lookup is by map key, which is what
    // `parsePrivilegedRoute` uses as the route id unless the route literal
    // sets its own `id` -- a route that does both would not be found here.
    const topNavStartSlot = React.useMemo(() => {
        const declared = matches
            .map(match => ROUTES[match.id]?.topNavStartSlot)
            .filter(Boolean);
        return declared[declared.length - 1];
    }, [matches]);

    const [availableApplications, setAvailableApplications] = useState<
        Application[]
    >([]);
    const [isEurekaPath, setIsEurekaPath] = useState(false);
    const [activeApplication, setActiveApplication] = useState<Application>(
        ApplicationList[Applications.INSIGHTS],
    );
    const [applicationSwitcherConfig, setApplicationSwitcherConfig] = useState<
        NavLinkConfig[]
    >([]);

    const goToApplicationHome = (applicationID: Applications) => {
        if (
            applicationID === 'admin' &&
            sessionService.isOrgUiEnabled() &&
            sessionService.isOrgPerUrlEnabled()
        ) {
            const currentPathBeforeHash = `${
                window.location.href.split('#')[0]
            }#`;
            let path: string =
                currentPathBeforeHash +
                (`${ApplicationList[applicationID].homePath}` || '/');
            path = removeOverrideOrgId(path);
            if (window.location.pathname === path) {
                window.location.reload();
            } else {
                // Pre-existing call; this PR only brings the file into lint scope.
                // eslint-disable-next-line no-restricted-syntax
                window.location.replace(path);
            }
        } else if (
            navService.getCurrentPathByLocationHash(false) ===
            ApplicationList[applicationID].homePath
        ) {
            window.location.reload();
        } else {
            navService.goToUrl(`${ApplicationList[applicationID].homePath}`);
        }
    };

    const getTargetForApplication = (applicationId: Applications) => {
        const titleIconId = ApplicationList[applicationId].titleIconId;
        let target = {
            callback: () => {
                goToApplicationHome(applicationId);
            },
            routerURI: `${ApplicationList[applicationId].homePath}`,
        } as NavLinkCallback;
        if (titleIconId && titleIconId !== null) {
            target = {
                ...target,
                titleIconId,
            } as NavLinkWithTitleIconAndCallback;
        }
        return target;
    };

    useEffect(() => {
        if (currentAppRoute.id !== Applications.CORE) {
            setActiveApplication(
                ApplicationList[currentAppRoute.id as Applications],
            );
        }
    }, [ApplicationList, currentAppRoute]);

    useEffect(() => {
        const pathName = (location.pathname as string) || '';
        setIsEurekaPath(pathName.includes('/insights/eureka'));
    }, [location, activeApplication]);

    useEffect(() => {
        (async () => {
            const newList: Application[] = [];

            await Promise.all(
                Object.values(ApplicationList).map(
                    async (application, index) => {
                        if (application.id === Applications.CORE) {
                            return;
                        }

                        try {
                            await Promise.all(application.privileges);
                            newList[index] = application;
                        } catch (e) {
                            Logger.warn(e);
                            newList[index] = null;
                        }
                    },
                ),
            );

            setAvailableApplications(newList.filter(app => !!app));
        })();
    }, [ApplicationList]);

    useEffect(() => {
        const applicationConfig = availableApplications.map(
            application =>
                ({
                    name: t(application.title as any),
                    id: application.id,
                    description: t(application.description as any),
                    icon: application.icon,
                    target: getTargetForApplication(application.id),
                    pageContext: undefined,
                } as NavLinkConfig),
        );
        setApplicationSwitcherConfig(applicationConfig);
    }, [t, availableApplications]);

    const showSearchBar =
        sessionService.isEurekaEnabled() || sessionService.isSaaSCluster();

    const getLeftNavThemeType = () =>
        getComputedNavThemeType(customStylingService.getConfig(), true);

    return (
        <GlobalNavV3
            sessionService={sessionService}
            renderSearchBar={showSearchBar}
            homeButtonConfig={{
                name: '',
                target: {
                    callback: () => goToApplicationHome(activeApplication.id),
                    routerURI: `${
                        ApplicationList[activeApplication.id as Applications]
                            .homePath
                    }`,
                } as NavLinkCallback,
            }}
            activeApplicationID={activeApplication.id}
            applicationSwitcherConfig={applicationSwitcherConfig}
            shouldShowPendoHelp={shouldShowPendoHelp}
            useAddUserToModeMutation={useAddUserToModeMutation}
            useGetAnalystStudioAccessInfoLazyQuery={
                useGetAnalystStudioAccessInfoLazyQuery
            }
            isCopilotEnabled={isCopilotEnabled}
            customQuickLinks={customQuickLinks}
            isEurekaPath={isEurekaPath}
            themeType={getLeftNavThemeType()}
            topNavStartSlot={topNavStartSlot}
        />
    );
};
export default NavContainer;
